import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Staff Mode picker — an admin selects a staff member to preview (read-only).
// Fetches the tenant's active staff from /staff-mode/targets and, on select,
// hands the chosen id up to App.js's enterStaffMode().
export default function StaffModePicker({ onClose, onEnter }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [entering, setEntering] = useState(null); // id currently being entered

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/staff-mode/targets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (alive) setStaff(res.data || []);
      } catch (e) {
        if (alive) setError(e.response?.data?.error || 'Failed to load staff list');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const nameOf = (s) =>
    s.display_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username;

  const term = q.trim().toLowerCase();
  const filtered = !term
    ? staff
    : staff.filter(s =>
        nameOf(s).toLowerCase().includes(term) ||
        (s.username || '').toLowerCase().includes(term) ||
        (s.email || '').toLowerCase().includes(term) ||
        (s.department || '').toLowerCase().includes(term)
      );

  const initials = (s) =>
    ((s.first_name?.[0] || '') + (s.last_name?.[0] || '')).toUpperCase() ||
    (s.username?.[0] || '?').toUpperCase();

  const handleEnter = async (s) => {
    setEntering(s.id);
    try {
      await onEnter(s.id);
    } finally {
      setEntering(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', zIndex:4000,
               display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width:'480px', maxWidth:'100%', maxHeight:'82vh', background:'#fff',
                 borderRadius:'14px', boxShadow:'0 24px 60px rgba(0,0,0,0.28)',
                 display:'flex', flexDirection:'column', overflow:'hidden' }}
      >
        <div style={{ padding:'18px 20px 12px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <h3 style={{ margin:0, fontSize:'17px', fontWeight:700, color:'#0f172a' }}>Enter Staff Mode</h3>
            <button onClick={onClose} aria-label="Close"
              style={{ border:'none', background:'transparent', fontSize:'22px', lineHeight:1,
                       color:'#94a3b8', cursor:'pointer' }}>×</button>
          </div>
          <p style={{ margin:'6px 0 0', fontSize:'12.5px', color:'#64748b' }}>
            Preview the portal exactly as a staff member sees it — sidebar, dashboards, and their data.
            This is <strong>read-only</strong>: nothing you do can change their data.
          </p>
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search name, username, email, department…"
            style={{ marginTop:'12px', width:'100%', boxSizing:'border-box', padding:'9px 12px',
                     border:'1px solid #e2e8f0', borderRadius:'9px', fontSize:'13.5px', outline:'none' }}
          />
        </div>

        <div style={{ overflowY:'auto', padding:'4px 10px 12px', borderTop:'1px solid #f1f5f9' }}>
          {loading && <div style={{ padding:'24px', textAlign:'center', color:'#94a3b8', fontSize:'13px' }}>Loading…</div>}
          {error && !loading && <div style={{ padding:'18px', color:'#dc2626', fontSize:'13px' }}>{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div style={{ padding:'24px', textAlign:'center', color:'#94a3b8', fontSize:'13px' }}>No matching staff.</div>
          )}
          {!loading && !error && filtered.map(s => (
            <button
              key={s.id}
              onClick={() => handleEnter(s)}
              disabled={entering !== null}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', padding:'9px 10px',
                       border:'none', background:'transparent', borderRadius:'9px', cursor:'pointer',
                       textAlign:'left', opacity: entering && entering !== s.id ? 0.5 : 1 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width:'34px', height:'34px', borderRadius:'50%', flexShrink:0,
                            background:'#e0e7ff', color:'#4338ca', fontSize:'12px', fontWeight:700,
                            display:'flex', alignItems:'center', justifyContent:'center' }}>
                {initials(s)}
              </div>
              <div style={{ minWidth:0, flex:1 }}>
                <div style={{ fontSize:'13.5px', fontWeight:600, color:'#0f172a', whiteSpace:'nowrap',
                              overflow:'hidden', textOverflow:'ellipsis' }}>
                  {nameOf(s)}
                  {s.role === 'admin' && (
                    <span style={{ marginLeft:'7px', fontSize:'10px', fontWeight:700, color:'#7c3aed',
                                   background:'#f3e8ff', padding:'1px 6px', borderRadius:'6px' }}>ADMIN</span>
                  )}
                </div>
                <div style={{ fontSize:'11.5px', color:'#94a3b8', whiteSpace:'nowrap',
                              overflow:'hidden', textOverflow:'ellipsis' }}>
                  {s.department ? `${s.department} · ` : ''}{s.email || s.username}
                </div>
              </div>
              {entering === s.id
                ? <span style={{ fontSize:'11.5px', color:'#7c3aed', fontWeight:600 }}>Entering…</span>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
